import React, { useState, useEffect, useRef } from "react";
import { useHistory, withRouter } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";
import SellTourConfigurationView from "./sellTourConfigurationView";
import styles from "./sellTourConfigurationStyles";
import ApiTour from "../../../API/ApiTour";
import ApiUser from "../../../API/ApiUser";
import { withTranslation } from "react-i18next";
import validator from "validator";
import { isQuillEmpty } from "./components/buyerEmailMessageEditor";
import validateTourIcon from "../../../utils/tourIconValidation";
import {
  DEFAULT_SELLER_COUNTRY,
  formatSellerAddress,
  parseSellerAddress,
} from "../../../utils/sellerAddress";
import {
  DEFAULT_TAX_PERCENTAGE,
  MAX_BUYER_EMAIL_TEXT_LENGTH,
  MAX_TAX_PERCENTAGE,
  MAX_TOUR_PRICE,
  MIN_TAX_PERCENTAGE,
  MIN_TOUR_PRICE,
} from "../../../constants/monetization";

// apiClient renvoie les erreurs sous forme d'objet { status, data } au lieu de
// throw : le corps utile peut être une chaîne ou un objet selon la route.
const extractApiMessage = (data) => {
  if (!data) return "";
  if (typeof data === "string") return data;
  return data.error || data.message || "";
};

const SellTourConfigurationComponent = (props) => {
  const { classes, t } = props;
  const history = useHistory();
  const legalInfoRef = useRef(null);

  const [state, setState] = useState({
    isMonetized: false,
    price: 15,
    tourTitle: "",
    stripeConnectComissionFixed: 0,
    stripeConnectCommissionPercent: 0,
    taxRateOptions: [],
    // Volontairement null tant que le vendeur n'a pas tranché : un parcours
    // monétisé avant cette feature n'a pas de TaxRateOptionId et on ne veut
    // surtout pas lui choisir une TVA à sa place.
    taxRateOptionId: null,
    legalInfo: {
      companyName: "",
      companySiret: "",
      vatNumber: "",
      address: "",
      complete: false,
      loaded: false,
    },
    legalForm: {
      companyName: "",
      companySiret: "",
      vatNumber: "",
      street: "",
      zipCode: "",
      city: "",
      country: DEFAULT_SELLER_COUNTRY,
    },
    isSavingLegalInfo: false,
    highlightLegalInfo: false,
    missingLegalFields: [],
    isLegalConfirmOpen: false,
    isTaxFormOpen: false,
    newTaxRate: { country: "FR", percentage: "" },
    isConfirmOpen: false,
    isCreatingTaxRate: false,
    isSaving: false,
    showMissingTaxWarning: false,
    contactEmail: "",
    iconUrl: "",
    isUploadingIcon: false,
    isDeletingIcon: false,
    buyerEmailCustomText: "",
    isSavingBuyerEmailText: false,
    errorMessage: "",
    successMessage: "",
  });

  useEffect(() => {
    document.title = "Configuration de vente | Landing Zone";
    getTourConfiguration();
    getTaxRateOptions();
    getSellerLegalInfo();
  }, []);

  const getTourId = () =>
    new URLSearchParams(props.location.search).get("tourId");

  const getTourConfiguration = async () => {
    const tourId = getTourId();
    if (!tourId) return;

    const response = await ApiTour.getTour(tourId);
    if (response.status === 200) {
      setState((prevState) => ({
        ...prevState,
        isMonetized: !response.data.monetizationPaused || false,
        price: response.data.tourMonetizationPrice || 0,
        tourTitle: response.data.title || "",
        taxRateOptionId: response.data.TaxRateOptionId ?? null,
        contactEmail: response.data.contactEmail || "",
        iconUrl: response.data.emailIcon?.signedUrl || "",
        buyerEmailCustomText: response.data.buyerEmailCustomText || "",
        stripeConnectComissionFixed: response.data.StripeConnectComissionFix || 0,
        stripeConnectCommissionPercent:
          response.data.stripeConnectCommissionPercent || 0,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        errorMessage: t("sell_tour_configuration.error.load_failed"),
      }));
    }
  };

  const getTaxRateOptions = async () => {
    const response = await ApiTour.getTaxRateOptions();
    if (response.status === 200 && Array.isArray(response.data)) {
      setState((prevState) => ({
        ...prevState,
        taxRateOptions: response.data.filter((option) => option.active !== false),
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        errorMessage: t("tax_rate.error.load_failed"),
      }));
    }
  };

  const getSellerLegalInfo = async () => {
    const response = await ApiUser.getSellerLegalInfo();
    if (response.status === 200 && response.data) {
      const address = parseSellerAddress(response.data.address);
      setState((prevState) => ({
        ...prevState,
        legalInfo: { ...response.data, loaded: true },
        legalForm: {
          companyName: response.data.companyName || "",
          companySiret: response.data.companySiret || "",
          vatNumber: response.data.vatNumber || "",
          ...address,
        },
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        legalInfo: { ...prevState.legalInfo, loaded: true },
      }));
    }
  };

  const handleMonetizationChange = (event) => {
    const checked = event.target.checked;
    setState((prevState) => ({
      ...prevState,
      isMonetized: checked,
      // On n'alerte sur la taxe manquante qu'à partir du moment où le vendeur
      // cherche réellement à activer la vente.
      showMissingTaxWarning: checked && prevState.taxRateOptionId === null,
    }));
  };

  const handlePriceChange = (event) => {
    const value = parseFloat(event.target.value);
    setState((prevState) => ({
      ...prevState,
      price: isNaN(value) ? "" : value,
    }));
  };

  const handleContactEmailChange = (event) => {
    const value = event.target.value;
    setState((prevState) => ({ ...prevState, contactEmail: value }));
  };

  const handleTaxRateChange = (event) => {
    const value = event.target.value;
    setState((prevState) => ({
      ...prevState,
      taxRateOptionId: value === "" ? null : Number(value),
      showMissingTaxWarning: false,
    }));
  };

  const handleLegalFormChange = (event) => {
    const { name, value } = event.target;
    setState((prevState) => ({
      ...prevState,
      legalForm: { ...prevState.legalForm, [name]: value },
      // Le champ corrigé cesse d'être signalé dès la saisie.
      missingLegalFields: prevState.missingLegalFields.filter(
        (field) => field !== name
      ),
    }));
  };

  const getMissingLegalFields = () =>
    [
      "companyName",
      "companySiret",
      "vatNumber",
      "street",
      "zipCode",
      "city",
      "country",
    ].filter((field) => !(state.legalForm[field] || "").trim());

  const handleSubmitLegalInfo = () => {
    const missing = getMissingLegalFields();
    if (missing.length) {
      setState((prevState) => ({
        ...prevState,
        missingLegalFields: missing,
        errorMessage: t("seller_legal_info.error.all_required"),
      }));
      return;
    }

    // Le SIRET et le n° de TVA figureront sur les prochaines factures : on
    // fait confirmer leur modification, mais pas celle de l'adresse.
    const identityChanged =
      state.legalForm.companySiret.trim() !== (state.legalInfo.companySiret || "") ||
      state.legalForm.vatNumber.trim() !== (state.legalInfo.vatNumber || "");

    if (identityChanged) {
      setState((prevState) => ({ ...prevState, isLegalConfirmOpen: true }));
      return;
    }

    saveLegalInfo();
  };

  const handleCancelLegalConfirm = () => {
    setState((prevState) => ({ ...prevState, isLegalConfirmOpen: false }));
  };

  const saveLegalInfo = async () => {
    setState((prevState) => ({ ...prevState, isSavingLegalInfo: true }));

    const response = await ApiUser.saveSellerLegalInfo(
      state.legalForm.companyName.trim(),
      state.legalForm.companySiret.trim(),
      state.legalForm.vatNumber.trim(),
      formatSellerAddress(state.legalForm)
    );

    if (response.status === 200 || response.status === 201) {
      setState((prevState) => ({
        ...prevState,
        isSavingLegalInfo: false,
        isLegalConfirmOpen: false,
        highlightLegalInfo: false,
        missingLegalFields: [],
        successMessage: t("seller_legal_info.success.saved"),
      }));
      getSellerLegalInfo();
      return;
    }

    // Le back ne dit pas quel champ manque : on remet en évidence ceux que le
    // formulaire considère vides.
    setState((prevState) => ({
      ...prevState,
      isSavingLegalInfo: false,
      isLegalConfirmOpen: false,
      missingLegalFields:
        response.status === 400 ? getMissingLegalFields() : prevState.missingLegalFields,
      errorMessage:
        response.status === 400
          ? t("seller_legal_info.error.all_required")
          : t("seller_legal_info.error.unknown"),
    }));
  };

  const handleToggleTaxForm = () => {
    setState((prevState) => ({
      ...prevState,
      isTaxFormOpen: !prevState.isTaxFormOpen,
      newTaxRate: { country: "FR", percentage: "" },
    }));
  };

  const handleNewTaxRateChange = (event) => {
    const { name, value } = event.target;
    setState((prevState) => ({
      ...prevState,
      newTaxRate: { ...prevState.newTaxRate, [name]: value },
    }));
  };

  const handleSubmitNewTaxRate = () => {
    const percentage = Number(state.newTaxRate.percentage);

    if (
      state.newTaxRate.percentage === "" ||
      isNaN(percentage) ||
      percentage < MIN_TAX_PERCENTAGE ||
      percentage > MAX_TAX_PERCENTAGE
    ) {
      setState((prevState) => ({
        ...prevState,
        errorMessage: t("tax_rate.percentage_range_error"),
      }));
      return;
    }

    // Un taux différent des 20% français est le cas le plus à risque d'erreur
    // de saisie : on fait confirmer avant de créer l'option.
    if (percentage !== DEFAULT_TAX_PERCENTAGE) {
      setState((prevState) => ({ ...prevState, isConfirmOpen: true }));
      return;
    }

    createTaxRateOption();
  };

  const handleCancelConfirm = () => {
    setState((prevState) => ({ ...prevState, isConfirmOpen: false }));
  };

  const createTaxRateOption = async () => {
    const percentage = Number(state.newTaxRate.percentage);
    const country = state.newTaxRate.country;

    setState((prevState) => ({ ...prevState, isCreatingTaxRate: true }));

    const response = await ApiTour.createTaxRateOption({
      country,
      percentage,
      displayName: t("tax_rate.generated_display_name", {
        country,
        percentage,
      }),
    });

    if ((response.status === 201 || response.status === 200) && response.data) {
      const created = response.data;
      setState((prevState) => ({
        ...prevState,
        isCreatingTaxRate: false,
        isConfirmOpen: false,
        isTaxFormOpen: false,
        newTaxRate: { country: "FR", percentage: "" },
        taxRateOptions: [...prevState.taxRateOptions, created],
        taxRateOptionId: created.id,
        showMissingTaxWarning: false,
        successMessage: t("tax_rate.success.created"),
      }));
      return;
    }

    setState((prevState) => ({
      ...prevState,
      isCreatingTaxRate: false,
      isConfirmOpen: false,
      errorMessage:
        response.status === 400
          ? t("tax_rate.percentage_range_error")
          : t("tax_rate.error.create_failed"),
    }));
  };

  const handleSelectIconFile = async (file) => {
    const tourId = getTourId();
    if (!tourId) return;

    const { errorKey } = await validateTourIcon(file);
    if (errorKey) {
      setState((prevState) => ({ ...prevState, errorMessage: t(errorKey) }));
      return;
    }

    setState((prevState) => ({ ...prevState, isUploadingIcon: true }));

    const response = await ApiTour.uploadTourEmailIcon(tourId, file);

    if (response.status === 200 && response.data?.iconUrl) {
      setState((prevState) => ({
        ...prevState,
        isUploadingIcon: false,
        iconUrl: response.data.iconUrl,
        successMessage: t("tour_email_icon.success.uploaded"),
      }));
      return;
    }

    const message = extractApiMessage(response.data).toLowerCase();

    setState((prevState) => ({
      ...prevState,
      isUploadingIcon: false,
      errorMessage: message.includes("only png or jpeg")
        ? t("tour_email_icon.error.invalid_type")
        : message.includes("no icon file provided")
          ? t("tour_email_icon.error.no_file")
          : response.status === 403
            ? t("tour_email_icon.error.forbidden")
            : t("tour_email_icon.error.upload_failed"),
    }));
  };

  const handleDeleteIcon = async () => {
    const tourId = getTourId();
    if (!tourId) return;

    setState((prevState) => ({ ...prevState, isDeletingIcon: true }));

    const response = await ApiTour.deleteTourEmailIcon(tourId);

    if (response.status === 200) {
      setState((prevState) => ({
        ...prevState,
        isDeletingIcon: false,
        iconUrl: "",
        successMessage: t("tour_email_icon.success.removed"),
      }));
      return;
    }

    setState((prevState) => ({
      ...prevState,
      isDeletingIcon: false,
      errorMessage:
        response.status === 403
          ? t("tour_email_icon.error.forbidden")
          : t("tour_email_icon.error.delete_failed"),
    }));
  };

  const handleBuyerEmailTextChange = (html) => {
    setState((prevState) => ({ ...prevState, buyerEmailCustomText: html }));
  };

  const handleSaveBuyerEmailText = async () => {
    const tourId = getTourId();
    if (!tourId) return;

    // Un éditeur "vidé" laisse un <p><br></p> : on l'envoie comme chaîne vide
    // pour que le back supprime réellement le message.
    const payload = isQuillEmpty(state.buyerEmailCustomText)
      ? ""
      : state.buyerEmailCustomText;

    if (payload.length > MAX_BUYER_EMAIL_TEXT_LENGTH) {
      setState((prevState) => ({
        ...prevState,
        errorMessage: t("buyer_email_message.error.too_long", {
          max: MAX_BUYER_EMAIL_TEXT_LENGTH,
        }),
      }));
      return;
    }

    setState((prevState) => ({ ...prevState, isSavingBuyerEmailText: true }));

    const response = await ApiTour.updateBuyerEmailCustomText(tourId, payload);

    if (response.status === 200) {
      setState((prevState) => ({
        ...prevState,
        isSavingBuyerEmailText: false,
        // Le back peut avoir retiré des balises : sa version fait foi.
        buyerEmailCustomText: response.data?.buyerEmailCustomText || "",
        successMessage: payload
          ? t("buyer_email_message.success.saved")
          : t("buyer_email_message.success.cleared"),
      }));
      return;
    }

    setState((prevState) => ({
      ...prevState,
      isSavingBuyerEmailText: false,
      errorMessage: t("buyer_email_message.error.save_failed"),
    }));
  };

  const focusLegalInfo = () => {
    setState((prevState) => ({ ...prevState, highlightLegalInfo: true }));
    if (legalInfoRef.current) {
      legalInfoRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const handleSave = async () => {
    const tourId = getTourId();
    if (!tourId) return;

    const contactEmail = state.contactEmail.trim();

    // On ne valide qu'à l'activation : mettre la vente en pause ne doit pas
    // être bloqué par un email de contact absent ou mal saisi.
    if (state.isMonetized && !validator.isEmail(contactEmail)) {
      setState((prevState) => ({
        ...prevState,
        errorMessage: contactEmail
          ? t("sell_tour_configuration.error.contact_email_invalid")
          : t("sell_tour_configuration.error.contact_email_required"),
      }));
      return;
    }

    setState((prevState) => ({ ...prevState, isSaving: true }));

    const response = await ApiTour.updateTourMonetization(tourId, {
      price: state.price,
      monetizationPaused: !state.isMonetized,
      taxRateOptionId: state.taxRateOptionId,
      // Omis plutôt qu'envoyé vide : sur une désactivation d'un parcours sans
      // email de contact, une chaîne vide risquerait le 400 "not a valid
      // email" et empêcherait de mettre la vente en pause.
      contactEmail: contactEmail || undefined,
    });

    if (response.status === 200) {
      setState((prevState) => ({
        ...prevState,
        isSaving: false,
        successMessage: t("sell_tour_configuration.success.saved"),
      }));
      setTimeout(() => {
        history.push("/");
      }, 2000);
      return;
    }

    // apiClient ne throw pas : sans ce traitement explicite, les 400/403 du
    // back passaient totalement inaperçus côté vendeur.
    const message = extractApiMessage(response.data).toLowerCase();

    if (message.includes("tax rate must be selected")) {
      setState((prevState) => ({
        ...prevState,
        isSaving: false,
        showMissingTaxWarning: true,
        errorMessage: t("sell_tour_configuration.error.tax_required"),
      }));
      return;
    }

    if (
      message.includes("legal billing info") ||
      message.includes("siret must be set")
    ) {
      setState((prevState) => ({
        ...prevState,
        isSaving: false,
        errorMessage: t("sell_tour_configuration.error.legal_info_required"),
      }));
      // L'identité a pu être complétée ailleurs entre-temps : on resynchronise
      // avant de renvoyer le vendeur vers le bloc.
      await getSellerLegalInfo();
      focusLegalInfo();
      return;
    }

    if (message.includes("contact email must be set")) {
      setState((prevState) => ({
        ...prevState,
        isSaving: false,
        errorMessage: t("sell_tour_configuration.error.contact_email_required"),
      }));
      return;
    }

    if (message.includes("contactemail is not a valid email")) {
      setState((prevState) => ({
        ...prevState,
        isSaving: false,
        errorMessage: t("sell_tour_configuration.error.contact_email_invalid"),
      }));
      return;
    }

    if (message.includes("tax rate option is not available")) {
      setState((prevState) => ({
        ...prevState,
        isSaving: false,
        taxRateOptionId: null,
        errorMessage: t("sell_tour_configuration.error.tax_unavailable"),
      }));
      getTaxRateOptions();
      return;
    }

    setState((prevState) => ({
      ...prevState,
      isSaving: false,
      errorMessage: t("sell_tour_configuration.error.save_failed"),
    }));
  };

  const resetErrorAlert = () => {
    setState((prevState) => ({ ...prevState, errorMessage: "" }));
  };

  const resetSuccessAlert = () => {
    setState((prevState) => ({ ...prevState, successMessage: "" }));
  };

  const selectedTaxOption =
    state.taxRateOptions.find((option) => option.id === state.taxRateOptionId) ||
    null;

  const price = Number(state.price) || 0;
  const platformCommission =
    price <= 10
      ? state.stripeConnectComissionFixed
      : price * (state.stripeConnectCommissionPercent / 100);
  const stripeCommission = price ? price * (1.5 / 100) + 0.25 : 0;
  const netTtc = price ? price - platformCommission - stripeCommission : 0;
  // Le prix est TTC : le HT dépend du taux choisi, il ne peut donc pas être
  // calculé tant qu'aucune option de taxe n'est sélectionnée.
  const netHt = selectedTaxOption
    ? (price - platformCommission) / (1 + selectedTaxOption.percentage / 100) -
      stripeCommission
    : null;

  const isPriceValid =
    price >= MIN_TOUR_PRICE && price <= MAX_TOUR_PRICE && !!state.price;
  const isLegalInfoComplete = !!state.legalInfo.complete;
  const isContactEmailValid = validator.isEmail(state.contactEmail.trim());
  // Désactiver la vente doit toujours rester possible : on ne bloque que
  // l'activation.
  const isSaveDisabled =
    state.isSaving ||
    (state.isMonetized &&
      (!isPriceValid ||
        state.taxRateOptionId === null ||
        !isLegalInfoComplete ||
        !isContactEmailValid));

  return (
    <SellTourConfigurationView
      classes={classes}
      state={state}
      legalInfoRef={legalInfoRef}
      commissions={{
        platform: platformCommission,
        stripe: stripeCommission,
        netTtc,
        netHt,
      }}
      selectedTaxOption={selectedTaxOption}
      isSaveDisabled={isSaveDisabled}
      isLegalInfoComplete={isLegalInfoComplete}
      isContactEmailValid={isContactEmailValid}
      handleContactEmailChange={handleContactEmailChange}
      handleMonetizationChange={handleMonetizationChange}
      handlePriceChange={handlePriceChange}
      handleTaxRateChange={handleTaxRateChange}
      handleLegalFormChange={handleLegalFormChange}
      handleSubmitLegalInfo={handleSubmitLegalInfo}
      handleConfirmLegalInfo={saveLegalInfo}
      handleCancelLegalConfirm={handleCancelLegalConfirm}
      handleToggleTaxForm={handleToggleTaxForm}
      handleNewTaxRateChange={handleNewTaxRateChange}
      handleSubmitNewTaxRate={handleSubmitNewTaxRate}
      handleSelectIconFile={handleSelectIconFile}
      handleDeleteIcon={handleDeleteIcon}
      handleBuyerEmailTextChange={handleBuyerEmailTextChange}
      handleSaveBuyerEmailText={handleSaveBuyerEmailText}
      handleCancelConfirm={handleCancelConfirm}
      handleConfirmCreateTaxRate={createTaxRateOption}
      handleSave={handleSave}
      resetErrorAlert={resetErrorAlert}
      resetSuccessAlert={resetSuccessAlert}
    />
  );
};

export default withStyles(styles)(
  withTranslation()(withRouter(SellTourConfigurationComponent))
);
